(function () {
  if (window.__cartPopupControllerLoaded) {
    return;
  }

  window.__cartPopupControllerLoaded = true;

  const SELECTORS = {
    popup: "cart-popup",
    popupById: "#cart-popup",
    drawer: "#cart-popup-drawer",
    overlay: ".cart-popup__overlay",
    closeTrigger: ".close-popup",
    quickCartTrigger: "a.quickCart",
    legacyPopup: "#addToCartPopup",
    quantityButton: ".cart-popup-item__stepper-btn",
    quantityInput: 'input[name="quantity"]',
    removeButton: ".cart-popup-item__remove",
    cartItem: "cart-popup-item",
    subscribeBadge: ".cart-popup-item__badge--subscribe",
    discountForm: ".discount-code-form",
    discountInput: 'input[name="discount"]',
    discountSubmit: '.cart-popup__discount-btn[type="submit"]',
    discountError: ".js-form-field-error",
    existingDiscountContainer: "[data-existing-discount]",
    discountItem: "[data-discount-item]",
    deliveryBlock: "#cart-popup-delivery",
    quickCartCount: "#quickCartItemCount, [data-cart-count]",
    quickCartPulse: "#quickCartUpdated",
  };

  const OPEN_CLASS = "cart-popup--open";
  const LOADING_CLASS = "cart-popup--loading";

  function debounce(callback, delay) {
    let timerId;

    return (...args) => {
      window.clearTimeout(timerId);
      timerId = window.setTimeout(() => {
        callback(...args);
      }, delay);
    };
  }

  class CartBase extends HTMLElement {
    query(selector, root = this) {
      return root.querySelector(selector);
    }

    queryAll(selector, root = this) {
      return Array.from(root.querySelectorAll(selector));
    }

    static query(selector, root = document) {
      return root.querySelector(selector);
    }

    static queryAll(selector, root = document) {
      return Array.from(root.querySelectorAll(selector));
    }
  }

  class CartPopupItem extends CartBase {
    connectedCallback() {
      if (this.__initialized) {
        return;
      }

      this.__initialized = true;
      this.onQuantityClick = this.onQuantityClick.bind(this);
      this.onRemoveClick = this.onRemoveClick.bind(this);
      this.onSubscribeClick = this.onSubscribeClick.bind(this);

      this.queryAll(SELECTORS.quantityButton).forEach((button) => {
        button.addEventListener("click", this.onQuantityClick);
      });

      const removeButton = this.query(SELECTORS.removeButton);
      if (removeButton) {
        removeButton.addEventListener("click", this.onRemoveClick);
      }

      const subscribeBadge = this.query(SELECTORS.subscribeBadge);
      if (subscribeBadge) {
        subscribeBadge.addEventListener("click", this.onSubscribeClick);
      }
    }

    disconnectedCallback() {
      this.queryAll(SELECTORS.quantityButton).forEach((button) => {
        button.removeEventListener("click", this.onQuantityClick);
      });

      const removeButton = this.query(SELECTORS.removeButton);
      if (removeButton) {
        removeButton.removeEventListener("click", this.onRemoveClick);
      }

      const subscribeBadge = this.query(SELECTORS.subscribeBadge);
      if (subscribeBadge) {
        subscribeBadge.removeEventListener("click", this.onSubscribeClick);
      }
    }

    get itemKey() {
      return this.dataset.itemKey;
    }

    get quantityInput() {
      return this.query(SELECTORS.quantityInput);
    }

    get currentQuantity() {
      return Number(this.quantityInput?.value || 1);
    }

    get selectedSellingPlanId() {
      return this.query(SELECTORS.subscribeBadge)?.dataset.sellingPlanId || this.dataset.subscriptionSellingPlanId;
    }

    get attachedGiftVariantId() {
      return this.dataset.attachedGiftVariantId || null;
    }

    onQuantityClick(event) {
      event.preventDefault();
      CartPopup.setLoading(true);

      const buttons = this.queryAll(SELECTORS.quantityButton);
      const isIncrease = buttons.indexOf(event.currentTarget) === buttons.length - 1;
      const nextQuantity = isIncrease
        ? this.currentQuantity + 1
        : Math.max(1, this.currentQuantity - 1);

      CartPopup.updateLineItem(this.itemKey, nextQuantity);
    }

    onRemoveClick(event) {
      event.preventDefault();

      
      const giftVariantId = this.attachedGiftVariantId;
      const giftItem = giftVariantId
        ? CartBase.queryAll(SELECTORS.cartItem).find(
            (item) => item !== this && item.dataset.variantId === giftVariantId && item.dataset.limitedGiftOf == this.dataset.productHandle
          )
        : null;

      if (giftItem) {
        // The gift was added atomically with this product (see
        // addProductWithGiftToCart in landing-greens.js) — remove both
        // together so the gift is never left orphaned in the cart.
        CartPopup.updateLineItems({
          [this.itemKey]: 0,
          [giftItem.itemKey]: 0,
        });
        return;
      }

      CartPopup.updateLineItem(this.itemKey, 0);
    }

    onSubscribeClick(event) {
      event.preventDefault();

      const sellingPlanId = this.selectedSellingPlanId;

      if (!sellingPlanId) {
        return;
      }

      CartPopup.updateLineItem(this.itemKey, this.currentQuantity, {
        sellingPlanId,
      });
    }
  }

  class CartPopup extends CartBase {
    connectedCallback() {
      CartPopup.instance = this;

      if (this.__initialized) {
        this.afterRender(this.classList.contains(OPEN_CLASS));
        return;
      }

      this.__initialized = true;
      this.onPopupClick = this.onPopupClick.bind(this);
      this.addEventListener("click", this.onPopupClick);

      this.afterRender(this.classList.contains(OPEN_CLASS));
      CartPopup.bindGlobalEvents();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onPopupClick);
    }

    onPopupClick(event) {
      if (event.target.closest(SELECTORS.overlay)) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target.closest(SELECTORS.closeTrigger)) {
        event.preventDefault();
        this.close();
      }
    }

    afterRender(shouldOpen) {
      this.updateDeliveryEstimate();
      this.setPopupState(Boolean(shouldOpen));
    }

    setPopupState(isOpen) {
      if (isOpen) {
        this.style.removeProperty("display");
        this.classList.add(OPEN_CLASS);
        this.setAttribute("aria-hidden", "false");
        document.body.style.overflow = "hidden";
        return true;
      }

      this.classList.remove(OPEN_CLASS);
      this.setAttribute("aria-hidden", "true");
      this.style.removeProperty("display");
      document.body.style.overflow = "";
      return false;
    }

    close() {
      this.setPopupState(false);
    }

    updateDeliveryEstimate() {
      const deliveryBlock = this.query(SELECTORS.deliveryBlock);

      if (!deliveryBlock) {
        return;
      }

      const text = this.query("p", deliveryBlock);

      if (!text) {
        return;
      }

      const minDays = Number(deliveryBlock.dataset.min || 0);
      const maxDays = Number(deliveryBlock.dataset.max || minDays || 0);
      const today = new Date();
      const minDate = CartPopup.addBusinessDays(today, minDays);
      const maxDate = CartPopup.addBusinessDays(today, maxDays);
      const template = deliveryBlock.dataset.template || "<strong>__from__</strong> – <strong>__to__</strong>";

      text.innerHTML = template
        .replace("__from__", CartPopup.formatDeliveryDate(minDate))
        .replace("__to__", CartPopup.formatDeliveryDate(maxDate));
    }

    static getInstance() {
      return CartBase.query(SELECTORS.popup) || CartBase.query(SELECTORS.popupById);
    }

    static async fetchPopupMarkup() {
      const response = await fetch("/?sections=cart-popup", {
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Popup refresh failed with status ${response.status}`);
      }

      const sections = await response.json();
      return sections["cart-popup"];
    }

    static parsePopupMarkup(html) {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = html.trim();
      return CartBase.query(SELECTORS.popup, wrapper) || CartBase.query(SELECTORS.popupById, wrapper);
    }

    static hideLegacyPopup() {
      const legacyPopup = CartBase.query(SELECTORS.legacyPopup);
      if (legacyPopup) {
        legacyPopup.classList.add("hidden");
      }
    }

    static isOpen() {
      const popup = CartPopup.getInstance();
      return Boolean(popup && popup.classList.contains(OPEN_CLASS));
    }

    static setLoading(isLoading) {
      const popup = CartPopup.getInstance();

      if (!popup) {
        return;
      }

      popup.classList.toggle(LOADING_CLASS, Boolean(isLoading));
    }

    static async open() {
      CartPopup.hideLegacyPopup();
      const popup = CartPopup.getInstance();

      if (popup) {
        popup.setPopupState(true);
        CartPopup.setLoading(true);
      }

      return CartPopup.refresh({ openAfterRender: true });
    }

    static close() {
      CartPopup.getInstance()?.close();
    }

    static async refresh(options = {}) {
      const shouldOpen =
        typeof options.openAfterRender === "boolean"
          ? options.openAfterRender
          : CartPopup.isOpen();

      if (CartPopup.refreshPromise) {
        return CartPopup.refreshPromise;
      }

      CartPopup.refreshPromise = (async () => {
        CartPopup.setLoading(true);

        const html = await CartPopup.fetchPopupMarkup();
        const nextPopup = CartPopup.parsePopupMarkup(html);
        const currentPopup = CartPopup.getInstance();

        if (!currentPopup || !nextPopup) {
          throw new Error("Popup markup is missing from the refreshed section.");
        }

        currentPopup.replaceWith(nextPopup);
        nextPopup.classList.remove(LOADING_CLASS);
        nextPopup.afterRender(shouldOpen);
        await CartPopup.updateCartCount();
        return nextPopup;
      })();

      try {
        return await CartPopup.refreshPromise;
      } catch (error) {
        console.error("Cart popup refresh error:", error);
        CartPopup.setLoading(false);
      } finally {
        CartPopup.refreshPromise = null;
      }
    }

    static async updateLineItem(itemKey, quantity, options = {}) {
      if (!itemKey) {
        return;
      }

      const payload = {
        id: itemKey,
        quantity,
      };

      if (options.sellingPlanId) {
        payload.selling_plan = options.sellingPlanId;
      }

      try {
        const data = await CartPopup.requestCartUpdate("/cart/change.js", payload);

        if (data.errors) {
          throw new Error(data.errors || "Cart update failed.");
        }

        await CartPopup.refresh({ openAfterRender: true });
      } catch (error) {
        console.error("Cart line update error:", error);
      }
    }

    static async updateLineItems(updates) {
      if (!updates || !Object.keys(updates).length) {
        return;
      }

      try {
        const data = await CartPopup.requestCartUpdate("/cart/update.js", { updates });

        if (data.errors) {
          throw new Error(data.errors || "Cart update failed.");
        }

        await CartPopup.refresh({ openAfterRender: true });
      } catch (error) {
        console.error("Cart line update error:", error);
      }
    }

    static async requestCartUpdate(endpoint, payload) {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || data.errors || "Cart request failed.");
      }

      return data;
    }

    static async updateCartCount() {
      try {
        const response = await fetch("/cart.js", {
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Cart count request failed with status ${response.status}`);
        }

        const cart = await response.json();
        const countElements = CartBase.queryAll(SELECTORS.quickCartCount);

        countElements.forEach((element) => {
          element.textContent = cart.item_count;
        });

        CartPopup.flashCartCount();
      } catch (error) {
        console.error("Failed to update cart count:", error);
      }
    }

    static flashCartCount() {
      const pulse = CartBase.query(SELECTORS.quickCartPulse);

      if (!pulse) {
        return;
      }

      pulse.classList.remove("hidden");
      window.clearTimeout(CartPopup.pulseTimer);
      CartPopup.pulseTimer = window.setTimeout(() => {
        pulse.classList.add("hidden");
      }, 900);
    }

    static addBusinessDays(startDate, daysToAdd) {
      const date = new Date(startDate);
      let remaining = Math.max(0, Number(daysToAdd) || 0);

      while (remaining > 0) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();

        if (day !== 0 && day !== 6) {
          remaining -= 1;
        }
      }

      return date;
    }

    static formatDeliveryDate(date) {
      const locale = document.documentElement.lang || navigator.language || "en";
      const formatter = new Intl.DateTimeFormat(locale, {
        weekday: "short",
        day: "2-digit",
        month: "short",
      });

      return formatter.format(date).replace(/,/g, "");
    }

    static onDocumentClick(event) {
      const quickCartTrigger = event.target.closest(SELECTORS.quickCartTrigger);

      if (quickCartTrigger) {
        event.preventDefault();
        CartPopup.open();
      }
    }

    static onDocumentKeydown(event) {
      if (event.key === "Escape" && CartPopup.isOpen()) {
        CartPopup.close();
      }
    }

    static observeLegacyPopup() {
      if (CartPopup.legacyObserver) {
        CartPopup.legacyObserver.disconnect();
      }

      CartPopup.legacyObserver = new MutationObserver(() => {
        const legacyPopup = CartBase.query(SELECTORS.legacyPopup);

        if (legacyPopup && !legacyPopup.classList.contains("hidden")) {
          legacyPopup.classList.add("hidden");
          CartPopup.open();
        }
      });

      CartPopup.legacyObserver.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    static bindGlobalEvents() {
      if (CartPopup.__globalEventsBound) {
        return;
      }

      CartPopup.__globalEventsBound = true;

      document.addEventListener("click", CartPopup.onDocumentClick);
      document.addEventListener("keydown", CartPopup.onDocumentKeydown);

      window.updateCartCount = () => CartPopup.updateCartCount();
      window.updateCartPopup = (options = {}) =>
        CartPopup.refresh({
          openAfterRender:
            typeof options.openAfterRender === "boolean"
              ? options.openAfterRender
              : true,
        });

      window.toggleCartPopup = (force) => {
        if (typeof force === "boolean") {
          if (force) {
            CartPopup.open();
            return true;
          }

          CartPopup.close();
          return false;
        }

        if (CartPopup.isOpen()) {
          CartPopup.close();
          return false;
        }

        CartPopup.open();
        return true;
      };

      window.CartPopup = {
        open: () => CartPopup.open(),
        close: () => CartPopup.close(),
        refresh: (options) => CartPopup.refresh(options),
      };

      CartPopup.observeLegacyPopup();
      CartPopup.updateCartCount();
    }
  }

  class CartDiscount extends CartBase {
    constructor() {
      super();
      this.DEBOUNCE_DELAY = 300;
    }

    connectedCallback() {
      if (this.__initialized) {
        return;
      }

      this.__initialized = true;
      this.form = this.query(SELECTORS.discountForm);
      this.input = this.query(SELECTORS.discountInput);
      this.submitButton = this.query(SELECTORS.discountSubmit);
      this.existingDiscountContainer = this.query(SELECTORS.existingDiscountContainer);
      this.errorText = this.query(SELECTORS.discountError);

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleRemoveClick = this.handleRemoveClick.bind(this);
      this.debouncedSubmit = debounce(this.handleSubmit, this.DEBOUNCE_DELAY);

      if (this.form) {
        this.form.addEventListener("submit", (event) => {
          event.preventDefault();
          this.debouncedSubmit();
        });
      }

      if (this.input) {
        this.input.addEventListener("input", this.handleInput);
      }

      if (this.existingDiscountContainer) {
        this.existingDiscountContainer.addEventListener("click", this.handleRemoveClick);
      }

      this.updateSubmitState();
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener("input", this.handleInput);
      }

      if (this.existingDiscountContainer) {
        this.existingDiscountContainer.removeEventListener("click", this.handleRemoveClick);
      }
    }

    get emptyMessage() {
      return this.dataset.emptyMessage || "Please enter a discount code";
    }

    get invalidMessage() {
      return this.dataset.invalidMessage || "This discount code could not be applied.";
    }

    get applyErrorMessage() {
      return this.dataset.applyErrorMessage || "Unable to update discount codes right now.";
    }

    handleInput() {
      this.clearError();
      this.updateSubmitState();
    }

    handleRemoveClick(event) {
      const discountItem = event.target.closest(SELECTORS.discountItem);

      if (!discountItem) {
        return;
      }

      event.preventDefault();
      this.removeDiscount(discountItem.dataset.code);
    }

    updateSubmitState() {
      if (!this.submitButton) {
        return;
      }

      const hasValue = Boolean(this.input?.value.trim());
      this.submitButton.disabled = !hasValue;
      this.submitButton.classList.toggle("_disabled", !hasValue);
    }

    getExistingDiscounts() {
      if (!this.existingDiscountContainer) {
        return [];
      }

      return this.queryAll(SELECTORS.discountItem, this.existingDiscountContainer)
        .map((item) => item.dataset.code)
        .filter(Boolean);
    }

    setLoading(isLoading) {
      if (!this.submitButton) {
        return;
      }

      this.submitButton.classList.toggle("loading", Boolean(isLoading));
      this.submitButton.disabled = Boolean(isLoading) || !this.input?.value.trim();
      this.submitButton.classList.toggle("_disabled", this.submitButton.disabled);
    }

    clearError() {
      if (!this.errorText) {
        return;
      }

      this.errorText.hidden = true;
      this.errorText.textContent = "";
      this.input?.classList.remove("__has-error");
    }

    errorMessage(message) {
      if (!this.errorText) {
        return;
      }

      this.errorText.textContent = message;
      this.errorText.hidden = false;
      this.input?.classList.add("__has-error");
      this.setLoading(false);
    }

    async handleSubmit() {
      const discountCodeValue = this.input?.value.trim();

      if (!discountCodeValue) {
        this.errorMessage(this.emptyMessage);
        return;
      }

      const existingDiscounts = this.getExistingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) {
        this.input.value = "";
        this.updateSubmitState();
        return;
      }

      this.setLoading(true);
      this.clearError();

      try {
        const data = await this.applyDiscount([...existingDiscounts, discountCodeValue]);
        const invalidDiscount = data.discount_codes?.find((discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        });

        if (invalidDiscount) {
          this.errorMessage(this.invalidMessage);
          return;
        }

        this.input.value = "";
        await CartPopup.refresh({ openAfterRender: true });
      } catch (error) {
        console.error("Discount apply error:", error);
        this.errorMessage(error.message || this.applyErrorMessage);
      } finally {
        this.setLoading(false);
      }
    }

    async applyDiscount(discounts) {
      return CartPopup.requestCartUpdate("/cart/update.js", {
        discount: discounts.join(","),
      });
    }

    async removeDiscount(codeToRemove) {
      const existingDiscounts = this.getExistingDiscounts();
      const updatedDiscounts = existingDiscounts.filter((code) => code !== codeToRemove);

      this.setLoading(true);
      this.clearError();

      try {
        await this.applyDiscount(updatedDiscounts);
        await CartPopup.refresh({ openAfterRender: true });
      } catch (error) {
        console.error("Discount remove error:", error);
        this.errorMessage(error.message || this.applyErrorMessage);
      } finally {
        this.setLoading(false);
      }
    }
  }

  if (!customElements.get("cart-popup-item")) {
    customElements.define("cart-popup-item", CartPopupItem);
  }

  if (!customElements.get("cart-discount")) {
    customElements.define("cart-discount", CartDiscount);
  }

  if (!customElements.get("cart-popup")) {
    customElements.define("cart-popup", CartPopup);
  }

  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-protection-toggle]");
    if (!btn) return;
    const expanded = btn.getAttribute("aria-expanded") === "true";
    btn.setAttribute("aria-expanded", String(!expanded));
    const bodyId = btn.getAttribute("aria-controls");
    const body = bodyId ? document.getElementById(bodyId) : btn.nextElementSibling;
    if (body) body.classList.toggle("is-collapsed", expanded);
  });
})();